import { Copy, Library, Loader2, Music2, Save, X } from 'lucide-react'
import type { FormEvent } from 'react'
import type { SunoStylePromptGenerationMode, SunoStylePromptSuggestion } from '../../../api/suno'
import { Button, IconButton, Pill, SegmentedControl } from '../../../components/ui'
import styles from '../../../pages/ToolsPage.module.css'
import type { SelectedBasePrompt } from './stylePromptGeneratorTypes'

type StylePromptWorkspaceProps = {
  clearAvailable: boolean
  description: string
  descriptionValid: boolean
  error: string | null
  isLoading: boolean
  generationMode: SunoStylePromptGenerationMode
  onClearBasePrompt: () => void
  onClearWorkspace: () => void
  onCopyText: (value: string, label: string) => Promise<void>
  onDescriptionChange: (value: string) => void
  onGenerationModeChange: (value: SunoStylePromptGenerationMode) => void
  onOpenPromptLibrary: () => void
  onSaveSuggestion: (suggestion: SunoStylePromptSuggestion) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
  selectedBasePrompt: SelectedBasePrompt | null
  suggestions: SunoStylePromptSuggestion[]
}

const cx = (...classes: Array<string | false | null | undefined>): string =>
  classes
    .filter(Boolean)
    .map((className) => styles[className as string] ?? className)
    .join(' ')

const shortPromptPreview = (value: string | null, fallback: string): string => {
  const trimmed = value?.trim()

  if (!trimmed) {
    return fallback
  }

  return trimmed.length > 180 ? `${trimmed.slice(0, 180).trim()}...` : trimmed
}

const generationModeItems: Array<{
  label: string
  value: SunoStylePromptGenerationMode
}> = [
  { label: 'Free', value: 'free' },
  { label: 'Hybrid', value: 'hybrid' },
  { label: 'Library', value: 'library_bound' },
  { label: 'Unusual', value: 'unusual' }
]

export const StylePromptWorkspace = ({
  clearAvailable,
  description,
  descriptionValid,
  error,
  isLoading,
  generationMode,
  onClearBasePrompt,
  onClearWorkspace,
  onCopyText,
  onDescriptionChange,
  onGenerationModeChange,
  onOpenPromptLibrary,
  onSaveSuggestion,
  onSubmit,
  selectedBasePrompt,
  suggestions
}: StylePromptWorkspaceProps) => (
  <section className={cx(styles['tools-layout'], styles['tools-detail-layout'])} aria-label="Creative tool workspace">
    <form className={styles['tools-form-panel']} onSubmit={(event) => void onSubmit(event)}>
      <div className={styles['tools-panel-heading']}>
        <span className={styles['tools-panel-icon']}>
          <Music2 size={18} />
        </span>
        <div>
          <h2>Music Brief</h2>
          <p>Turn free-form intent into concise Suno-ready style variants.</p>
        </div>
      </div>

      <div className={styles['tools-base-prompt-section']}>
        <div className={styles['tools-base-prompt-header']}>
          <span className="form-label">Base prompt</span>
          <Button className={cx(styles['tools-library-button'])} onClick={onOpenPromptLibrary}>
            <Library size={16} /> Library
          </Button>
        </div>
        {selectedBasePrompt ? (
          <div className={styles['tools-base-prompt-card']}>
            <div>
              <strong>{selectedBasePrompt.title}</strong>
              {selectedBasePrompt.description && <span>{selectedBasePrompt.description}</span>}
              <code>{shortPromptPreview(selectedBasePrompt.style_prompt, 'No style prompt preview')}</code>
            </div>
            <IconButton
              icon={<X size={15} />}
              label={`Clear base prompt ${selectedBasePrompt.title}`}
              onClick={onClearBasePrompt}
              title="Clear base prompt"
            />
          </div>
        ) : null}
      </div>

      <div className={styles['tools-generation-mode-section']}>
        <span className="form-label">Generation mode</span>
        <SegmentedControl
          ariaLabel="Style prompt generation mode"
          items={generationModeItems}
          layout="responsive"
          onChange={onGenerationModeChange}
          value={generationMode}
        />
      </div>

      <label className="form-label" htmlFor="suno-style-description">
        What should the track feel like?
      </label>
      <textarea
        className={cx('form-input', styles['tools-style-prompt'])}
        id="suno-style-description"
        maxLength={3000}
        onChange={(event) => onDescriptionChange(event.target.value)}
        placeholder="Хочу темный, но дорогой трек для ночной поездки: без рэпа, с женским вокальным хуком, ровный грув, не слишком агрессивный..."
        rows={9}
        value={description}
      />

      {error && <div className={styles['tools-error']}>{error}</div>}

      <div className={styles['tools-form-actions']}>
        <Button
          className={cx(styles['tools-generate-button'])}
          disabled={!descriptionValid || isLoading}
          type="submit"
          variant="primary"
        >
          {isLoading ? <Loader2 className="tools-spin" size={16} /> : <Music2 size={16} />}
          {isLoading ? 'Generating...' : 'Generate style prompts'}
        </Button>
        <Button
          className={cx(styles['tools-clear-button'])}
          disabled={!clearAvailable || isLoading}
          onClick={onClearWorkspace}
        >
          <X size={16} /> Clear
        </Button>
      </div>
    </form>

    <section className={styles['tools-results-panel']} aria-label="Generated Suno style prompts">
      <div className={styles['tools-results-heading']}>
        <div>
          <h2>Style Variants</h2>
          <p>Prompts in English, notes in Russian</p>
        </div>
        <Pill strong>{suggestions.length}/6</Pill>
      </div>

      {isLoading && (
        <div className={styles['tools-results-state']}>
          <Loader2 className="tools-spin" size={24} />
          <span>Generating Suno style variants...</span>
        </div>
      )}

      {!isLoading && suggestions.length === 0 && (
        <div className={styles['tools-results-state']}>
          <Music2 size={24} />
          <span>Describe the track you want to receive 6 Suno style prompts.</span>
        </div>
      )}

      {!isLoading && suggestions.length > 0 && (
        <ol className={styles['tools-suggestion-list']}>
          {suggestions.map((suggestion, index) => (
            <li className={cx(styles['tools-suggestion-item'], styles['tools-style-suggestion-item'])} key={`${suggestion.title}-${index}`}>
                <div className={styles['tools-style-suggestion-content']}>
                  <div className={styles['tools-style-suggestion-title-row']}>
                  <strong>{suggestion.title}</strong>
                  <IconButton
                    icon={<Save size={15} />}
                    label={`Save ${suggestion.title}`}
                    onClick={() => onSaveSuggestion(suggestion)}
                    title="Save prompt"
                  />
                </div>
                <div className={styles['tools-prompt-row']}>
                  <span>Style</span>
                  <code>{suggestion.style_prompt}</code>
                  <IconButton
                    icon={<Copy size={15} />}
                    label={`Copy style prompt ${suggestion.title}`}
                    onClick={() => void onCopyText(suggestion.style_prompt, 'style prompt')}
                    title="Copy style prompt"
                  />
                </div>
                {suggestion.exclude_prompt && (
                  <div className={styles['tools-prompt-row']}>
                    <span>Exclude</span>
                    <code>{suggestion.exclude_prompt}</code>
                    <IconButton
                      icon={<Copy size={15} />}
                      label={`Copy exclude prompt ${suggestion.title}`}
                      onClick={() => void onCopyText(suggestion.exclude_prompt ?? '', 'exclude prompt')}
                      title="Copy exclude prompt"
                    />
                  </div>
                )}
                <p>{suggestion.explanation}</p>
              </div>
            </li>
          ))}
        </ol>
      )}
    </section>
  </section>
)
